import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, doc, query, where, orderBy, getDocs } from "firebase/firestore";
import logo from "../assets/logo.jpg";
import Clients from "./Clients";
import Tasks from "./Tasks";
import Files from "./Files";
import Invoicing from "./Invoicing";
import TimeTracking from "./TimeTracking";
import Reports from "./Reports";
import EngagementLetters from "./EngagementLetters";
import QBO from "./QBO";
import Settings from "./Settings";
import Team from "./Team";
import TeamChat from "./TeamChat";
import Help from "./Help";
import GlobalSearch from "./GlobalSearch";
import Alerts, { useAlertCounts } from "./Alerts";
import AdvancedSearch from "./AdvancedSearch";
import Messages from "./Messages";

const ADMIN_EMAIL = "frank@ficsoriaccounting.com";

const NAV = [
  {id:"dashboard",label:"Dashboard",section:"Overview"},
  {id:"clients",label:"CRM & Clients",section:"Clients"},
  {id:"tasks",label:"Tasks & Workflow",section:"Clients"},
  {id:"files",label:"Secure Files",section:"Work"},
  {id:"letters",label:"Engagement Letters",section:"Work"},
  {id:"invoicing",label:"Invoicing",section:"Work"},
  {id:"time",label:"Time Tracking",section:"Work"},
  {id:"reports",label:"Reports",section:"Reporting"},
  {id:"qbo",label:"QBO Integration",section:"Reporting"},
  {id:"messages",label:"Messages",section:"Clients"},
  {id:"alerts",label:"Alerts",section:"Clients"},
  {id:"team",label:"Team",section:"Settings",expandable:true,children:[
    {id:"team-members",label:"Team Members"},
    {id:"team-chat",label:"Team Chat"},
  ]},
  {id:"settings",label:"Settings",section:"Settings"},
  {id:"team-chat",label:"Team Chat",section:"Settings"},
  {id:"help",label:"Help & Guide",section:"Settings"},
];

const TITLES = {dashboard:"Dashboard",clients:"CRM & Clients",tasks:"Tasks & Workflow",files:"Secure Files",letters:"Engagement Letters",invoicing:"Invoicing",time:"Time Tracking",reports:"Reports",qbo:"QBO Integration",team:"Team Members","team-members":"Team Members","team-chat":"Team Chat",settings:"Settings",alerts:"Alerts & Notifications",help:"Help & Guide",messages:"Messages","advanced-search":"Advanced Search"};

const S = {
  shell:{display:"flex",height:"100vh",fontFamily:"sans-serif",background:"#f5f5f0",color:"#1a1a18"},
  sidebar:{width:220,minWidth:220,background:"#fff",borderRight:"0.5px solid #e0dfd8",display:"flex",flexDirection:"column"},
  brand:{padding:"14px 16px 8px",borderBottom:"0.5px solid #e0dfd8",display:"flex",flexDirection:"column",alignItems:"center",gap:4},
  navSection:{padding:"10px 0"},
  navLabel:{fontSize:10,color:"#aaa",textTransform:"uppercase",letterSpacing:"0.08em",padding:"4px 16px 6px",fontWeight:500},
  navItem:(active)=>({display:"flex",alignItems:"center",padding:"7px 16px",fontSize:13,color:active?"#C0111F":"#555",cursor:"pointer",borderLeft:active?"2px solid #C0111F":"2px solid transparent",background:active?"#fdf0f0":"transparent",fontWeight:active?500:400}),
  main:{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"},
  topbar:{background:"#fff",borderBottom:"0.5px solid #e0dfd8",padding:"0 24px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between"},
  content:{flex:1,overflowY:"auto",padding:"20px 24px"},
  statCard:{background:"#f5f5f0",borderRadius:8,padding:"14px 16px"},
  btn:{fontSize:12,padding:"6px 14px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",color:"#1a1a18",cursor:"pointer"},
  btnPrimary:{fontSize:12,padding:"6px 14px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer"},
};



function DashboardHome({ onNavigate }) {
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(()=>{
    const unsubCl = onSnapshot(
      query(collection(db,"clients"),orderBy("name")),
      snap=>setClients(snap.docs.map(d=>d.data()).filter(d=>d.name))
    );
    const unsubT = onSnapshot(
      query(collection(db,"tasks"),orderBy("createdAt","desc")),
      snap=>{ setTasks(snap.docs.map(d=>({...d.data(),firestoreId:d.id}))); },
      err=>{ console.log("Tasks load error:", err.code); }
    );
    const unsubF = onSnapshot(
      query(collection(db,"files"),orderBy("uploadedAt","desc")),
      snap=>{ setFiles(snap.docs.map(d=>({...d.data(),firestoreId:d.id}))); },
      err=>{ console.log("Files load error:", err.code); }
    );
    const unsubI = onSnapshot(
      query(collection(db,"invoices"),orderBy("createdAt","desc")),
      snap=>{ setInvoices(snap.docs.map(d=>({...d.data(),firestoreId:d.id}))); },
      err=>{ console.log("Invoices load error (expected for non-admin):", err.code); }
    );
    return ()=>{ unsubCl(); unsubT(); unsubF(); unsubI(); };
  },[]);

  const openTasks = tasks.filter(t=>{ const s=t.status||"To Do"; return s!=="Done" && s!=="Completed"; });
  const doneTasks = tasks.filter(t=>t.status==="Completed"||t.status==="Done");
  const recentFiles = files.slice(0,5);
  const overdueInvoices = invoices.filter(i=>i.status==="Overdue");
  const activeClients = clients.filter(c=>c.portalAccepted).length;

  const PRIORITY_COLORS = {High:["#FAEEDA","#854F0B"],Med:["#E6F1FB","#185FA5"],Low:["#f5f5f0","#888"]};
  const STATUS_COLORS = {"To Do":["#f5f5f0","#555"],"In Progress":["#E6F1FB","#185FA5"],"Needs Review":["#FAEEDA","#854F0B"],"Done":["#EAF3DE","#3B6D11"]};
  const badge = (bg,c) => ({display:"inline-block",fontSize:10,padding:"2px 7px",borderRadius:20,background:bg,color:c,fontWeight:500});

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[
          ["Open Tasks", openTasks.length, "#C0111F", "tasks"],
          ["Completed Tasks", doneTasks.length, "#3B6D11", "tasks"],
          ["Overdue Invoices", overdueInvoices.length, overdueInvoices.length>0?"#A32D2D":"#3B6D11", "invoicing"],
          ["Active Clients", activeClients||"—", "#185FA5", "clients"],
        ].map(([l,v,c,nav])=>(
          <div key={l} style={{...S.statCard,cursor:"pointer"}} onClick={()=>onNavigate(nav)}>
            <div style={{fontSize:12,color:"#888",marginBottom:6}}>{l}</div>
            <div style={{fontSize:26,fontWeight:500,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:500}}>Open Tasks</div>
            <span style={{fontSize:11,color:"#C0111F",cursor:"pointer"}} onClick={()=>onNavigate("tasks")}>View all →</span>
          </div>
          {openTasks.length===0
            ?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"20px 0"}}>No open tasks.</div>
            :openTasks.slice(0,5).map(t=>(
              <div key={t.firestoreId} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 0",borderBottom:"0.5px solid #f0efe8",cursor:"pointer"}} onClick={()=>onNavigate("tasks")}>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:500}}>{t.title}</div>
                  <div style={{fontSize:11,color:"#888",marginTop:2}}>
                    {[t.client, t.assignedTo?"→ "+t.assignedTo:"", t.due?"Due "+new Date(t.due+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"}):""].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div style={{display:"flex",gap:4,flexShrink:0}}>
                  {t.priority&&<span style={badge(...(PRIORITY_COLORS[t.priority]||["#f5f5f0","#888"]))}>{t.priority}</span>}
                  <span style={badge(...(STATUS_COLORS[t.status||"To Do"]||["#f5f5f0","#555"]))}>{t.status||"To Do"}</span>
                </div>
              </div>
            ))
          }
          {openTasks.length>5&&<div style={{fontSize:11,color:"#C0111F",marginTop:8,cursor:"pointer"}} onClick={()=>onNavigate("tasks")}>+{openTasks.length-5} more tasks →</div>}
        </div>

        <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:500}}>Recent Files</div>
            <span style={{fontSize:11,color:"#C0111F",cursor:"pointer"}} onClick={()=>onNavigate("files")}>View all →</span>
          </div>
          {recentFiles.length===0
            ?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"20px 0"}}>No files uploaded yet.</div>
            :recentFiles.map(f=>(
              <div key={f.firestoreId} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"0.5px solid #f0efe8",cursor:"pointer"}} onClick={()=>onNavigate("files")}>
                <div style={{fontSize:16,flexShrink:0}}>
                  {f.name?.endsWith(".pdf")?"📄":f.name?.match(/\.(xlsx|xls|csv)$/)?"📊":f.name?.match(/\.(doc|docx)$/)?"📝":"📎"}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                  <div style={{fontSize:11,color:"#888"}}>{f.client} · {f.folderName||f.folder}</div>
                </div>
                <div style={{fontSize:11,color:"#aaa",flexShrink:0}}>{f.uploadedAt?new Date(f.uploadedAt).toLocaleDateString("en-US",{month:"short",day:"numeric"}):"—"}</div>
              </div>
            ))
          }
        </div>
      </div>

      {overdueInvoices.length>0&&(
        <div style={{background:"#fff",border:"0.5px solid #FCEBEB",borderRadius:12,padding:"14px 20px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:13,fontWeight:500,color:"#A32D2D"}}>⚠ Overdue Invoices</div>
            <span style={{fontSize:11,color:"#C0111F",cursor:"pointer"}} onClick={()=>onNavigate("invoicing")}>View all →</span>
          </div>
          {overdueInvoices.map(inv=>(
            <div key={inv.firestoreId} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"0.5px solid #f0efe8",fontSize:12}}>
              <span style={{fontWeight:500}}>{inv.client}</span>
              <span style={{color:"#888"}}>{inv.service}</span>
              <span style={{fontWeight:500,color:"#A32D2D"}}>{inv.amount}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ user, onLogout }) {
  const [page, setPage] = useState(()=>{
    const hash = window.location.hash.replace('#','').trim();
    const validPages = ["dashboard","clients","tasks","files","letters","invoicing","time","reports","qbo","messages","alerts","team","team-members","team-chat","settings","help","advanced-search"];
    if(hash && validPages.includes(hash)) return hash;
    return localStorage.getItem("ficsori_active_page")||"dashboard";
  });
  const [expandedNav, setExpandedNav] = useState(()=>new Set(["team"]));

  const [pendingFolderFilter, setPendingFolderFilter] = useState(null);
  const [advSearchQuery, setAdvSearchQuery] = useState("");
  const [allTasks, setAllTasks] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(()=>{
    const unsubC = onSnapshot(query(collection(db,"clients"),orderBy("name")), snap=>{
      setClients(snap.docs.map(d=>d.data().name).filter(Boolean));
    });
    return ()=>unsubC();
  },[]);

  useEffect(()=>{
    const q = query(collection(db,"tasks"), orderBy("createdAt","desc"));
    const unsub = onSnapshot(q, snap=>{
      setAllTasks(snap.docs.map(d=>({...d.data(), firestoreId:d.id})));
    }, err=>{ console.error("Tasks listener error:", err); });
    return ()=>unsub();
  },[]);

  const navigate = (p) => {
    setPage(p);
    localStorage.setItem("ficsori_active_page", p);
    window.history.replaceState(null, "", window.location.pathname + "#" + p);
    // Clear team chat notifications when visiting team-chat
    if(p === "team-chat"){
      getDocs(query(collection(db,"notifications"), where("type","==","task_assigned"), where("forAdmin","==",true), where("read","==",false)))
        .then(snap=>{
          const batch = snap.docs.filter(d=>(d.data().details||"").startsWith("#"));
          if(batch.length){
            import("firebase/firestore").then(({writeBatch:wb, doc:fd})=>{
              const b = wb(db);
              batch.forEach(d=>b.update(fd(db,"notifications",d.id),{read:true}));
              b.commit();
            });
          }
        }).catch(()=>{});
    }
  };

  // Listen for hash changes (e.g. clicking email links while portal is open)
  useEffect(()=>{
    const onHash = () => {
      const hash = window.location.hash.replace('#','').trim();
      if(hash) { setPage(hash); localStorage.setItem("ficsori_active_page", hash); }
    };
    window.addEventListener("hashchange", onHash);
    return ()=>window.removeEventListener("hashchange", onHash);
  },[]);



  const navigateToFolder = (folderName) => {
    const FOLDER_MAP = {
      'Bank Statements':'bank','Tax Returns':'tax','QBO / Bookkeeping':'bookkeeping',
      'Payroll':'payroll','Loan Statements':'loans','CFO Reports & Scorecards':'cfo',
      'Engagement Letters':'engagement','Corporate Docs':'corporate',
      'Checks & Receipt Images':'checks','Other / Miscellaneous':'other'
    };
    setPendingFolderFilter(FOLDER_MAP[folderName] || folderName);
    navigate('files');
  };
  const isAdmin = user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const { total: alertTotal, clientCount: alertClientCount, teamCount: alertTeamCount } = useAlertCounts();
  const [teamChatUnread, setTeamChatUnread] = useState(0);
  useEffect(()=>{
    // Check team chat unread from notifications collection
    const unsub = onSnapshot(
      query(collection(db,"notifications"), where("type","==","task_assigned"), where("forAdmin","==",true), where("read","==",false)),
      snap=>{
        const teamChatAlerts = snap.docs.filter(d=>{
          const details = d.data().details||"";
          return details.startsWith("#");
        });
        setTeamChatUnread(teamChatAlerts.length);
      }
    );
    return ()=>unsub();
  },[]);
  const [assignedClients, setAssignedClients] = useState(isAdmin ? null : undefined);

  useEffect(()=>{
    if(isAdmin){ setAssignedClients(null); return; }
    const q = query(collection(db,"team"), where("email","==",user.email.toLowerCase()));
    const unsub = onSnapshot(q, snap=>{
      if(!snap.empty){
        const member = snap.docs[0].data();
        setAssignedClients(member.assignedClients || []);
      } else {
        setAssignedClients([]);
      }
    }, err=>{ console.error(err); setAssignedClients([]); });
    return ()=>unsub();
  },[isAdmin, user.email]);

  const [teamMemberName, setTeamMemberName] = useState("Bookkeeper");
  useEffect(()=>{
    if(isAdmin || !user?.email) return;
    getDocs(query(collection(db,"team"), where("email","==",user.email.toLowerCase())))
      .then(snap=>{ if(!snap.empty) setTeamMemberName(snap.docs[0].data().name||"Bookkeeper"); })
      .catch(()=>{});
  },[isAdmin, user?.email]);

  const userName = isAdmin ? "Frank Ficsori" : teamMemberName;
  // Don't render until assignedClients is resolved for non-admin users
  if(!isAdmin && assignedClients === undefined){
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"sans-serif",background:"#f5f5f0",flexDirection:"column",gap:12}}>
        <div style={{width:32,height:32,border:"3px solid #e0dfd8",borderTop:"3px solid #C0111F",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
        <div style={{fontSize:13,color:"#888"}}>Loading your workspace...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const sections = [...new Set(NAV.map(n=>n.section))];
  const today = new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"});

  // Filter nav — non-admins don't see Team or Settings
  const visibleNav = NAV.filter(n => {
    if(assignedClients === undefined) return n.id === "dashboard";
    if(!isAdmin && (n.id==="settings" || n.id==="qbo" || n.id==="reports" || n.id==="invoicing" || n.id==="letters" || n.id==="messages")) return false;
    // help is visible to everyone
    if(!isAdmin && (n.id==="team-members" || n.id==="team")) return false;
    return true;
  });
  const visibleSections = [...new Set(visibleNav.map(n=>n.section))];


  const renderPage = () => {
    switch(page) {
      case "dashboard": return <DashboardHome onNavigate={navigate}/>;
      case "clients": return <Clients assignedClients={assignedClients} onNavigateToMessages={(clientName)=>{ localStorage.setItem("ficsori_message_client",clientName); navigate("messages"); }}/>;
      case "tasks": return <Tasks assignedClients={assignedClients} currentUser={user.email} currentUserName={userName} navigate={navigate} navigateToFolder={navigateToFolder}/>;
      case "files": return <Files assignedClients={assignedClients} userEmail={user.email} isAdmin={isAdmin} initialFolder={pendingFolderFilter} onFolderLoaded={()=>setPendingFolderFilter(null)}/>;
      case "letters": return isAdmin ? <EngagementLetters assignedClients={assignedClients}/> : <DashboardHome onNavigate={navigate}/>;
      case "invoicing": return isAdmin ? <Invoicing assignedClients={assignedClients}/> : <DashboardHome onNavigate={navigate}/>;
      case "time": return <TimeTracking assignedClients={assignedClients} currentUser={user.email}/>;
      case "reports": return <Reports/>;
      case "qbo": return <QBO/>;
      case "messages": return isAdmin ? <Messages isAdmin={true} currentUserName="Frank Ficsori" assignedClients={clients}/> : <DashboardHome onNavigate={navigate}/>;
      case "team": return isAdmin ? <Team/> : <DashboardHome onNavigate={navigate}/>;
      case "team-members": return isAdmin ? <Team/> : <DashboardHome onNavigate={navigate}/>;
      case "team-chat": return <TeamChat currentUserName={userName} currentUserEmail={user.email} tasks={[]}/>;
      case "alerts": return <Alerts isAdmin={isAdmin} currentUserEmail={user.email}/>;
      case "settings": return isAdmin ? <Settings user={user} onLogout={onLogout}/> : <DashboardHome onNavigate={navigate}/>;
      case "help": return <Help/>;
      default: return <DashboardHome onNavigate={navigate}/>;
    }
  };

  return (
    <div style={S.shell}>
      <div style={S.sidebar}>
        <div style={S.brand}>
          <img src={logo} alt="Ficsori Accounting" style={{height:110,objectFit:"contain",maxWidth:260}}/>
          <div style={{fontSize:10,color:"#aaa"}}>Client Portal</div>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {visibleSections.map(section=>(
            <div key={section} style={S.navSection}>
              <div style={S.navLabel}>{section}</div>
              {visibleNav.filter(n=>n.section===section).map(n=>{
                if(n.expandable){
                  const isExpanded = expandedNav.has(n.id);
                  return (
                    <React.Fragment key={n.id}>
                      <div style={{...S.navItem(page===n.id||page===n.id+"-members"||page===n.id+"-chat"),justifyContent:"space-between",display:"flex"}}
                        onClick={()=>{
                          setExpandedNav(prev=>{
                            const next=new Set(prev);
                            next.has(n.id)?next.delete(n.id):next.add(n.id);
                            return next;
                          });
                        }}>
                        <span>{n.label}</span>
                        <span style={{fontSize:10,opacity:0.6}}>{isExpanded?"▲":"▼"}</span>
                      </div>
                      {isExpanded&&n.children&&n.children.map(child=>(
                        <div key={child.id} style={{...S.navItem(page===child.id),paddingLeft:28,fontSize:12,display:"flex",justifyContent:"space-between",alignItems:"center"}} onClick={()=>navigate(child.id)}>
                          <span>{child.label}</span>
                          {child.id==="team-chat"&&teamChatUnread>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,padding:"1px 6px",borderRadius:10,fontWeight:500}}>{teamChatUnread}</span>}
                        </div>
                      ))}
                    </React.Fragment>
                  );
                }
                return (
                <div key={n.id} style={{...S.navItem(page===n.id),display:"flex",justifyContent:"space-between",alignItems:"center"}} onClick={()=>navigate(n.id)}>
                  <span>{n.label}</span>
                  {n.id==="alerts"&&alertTotal>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,padding:"1px 6px",borderRadius:10,fontWeight:500}}>{alertTotal}</span>}
                  {n.id==="messages"&&alertClientCount>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,padding:"1px 6px",borderRadius:10,fontWeight:500}}>{alertClientCount}</span>}
                  {n.id==="team-chat"&&teamChatUnread>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,padding:"1px 6px",borderRadius:10,fontWeight:500}}>{teamChatUnread}</span>}
                </div>
              );
              })}
            </div>
          ))}
        </div>
        <div style={{padding:"8px 16px 12px",borderTop:"0.5px solid #e0dfd8"}}>
          <div style={{fontSize:11,color:"#888",marginBottom:2}}>{user.email}</div>
          {!isAdmin && assignedClients!==null && assignedClients!==undefined && (
            <div style={{fontSize:10,color:"#aaa"}}>{(assignedClients||[]).length} client{(assignedClients||[]).length!==1?"s":""} assigned</div>
          )}
        </div>
      </div>
      <div style={S.main}>
        <div style={S.topbar}>
          <div style={{fontSize:15,fontWeight:500}}>{TITLES[page]}</div>
          <GlobalSearch
            isAdmin={isAdmin}
            onNavigate={(dest, query)=>{
              if(dest==="advanced-search"){ setAdvSearchQuery(query||""); navigate("advanced-search"); }
              else if(dest==="files" && query){ localStorage.setItem("ficsori_selected_client", query); navigate("files"); }
              else navigate(dest);
            }}
          />
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:12,color:"#888"}}>{today}</span>
            <button style={S.btnPrimary} onClick={onLogout}>Sign Out</button>
          </div>
        </div>
        <div style={S.content}>{renderPage()}</div>
      </div>
    </div>
  );
}
